import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { cx } from './cx'

export interface TabItem<K extends string> {
  key: K
  label: ReactNode
  count?: number
}

export interface TabsProps<K extends string> {
  tabs: Array<TabItem<K>>
  value: K
  onChange: (key: K) => void
  label: string
  // id prefix for panels: tab `key` controls the element with id `${idPrefix}-panel-${key}`
  idPrefix?: string
}

/**
 * WAI-ARIA tablist. Arrow keys, Home and End move between tabs and select them;
 * only the selected tab is in the Tab order.
 */
export function Tabs<K extends string>({ tabs, value, onChange, label, idPrefix }: TabsProps<K>) {
  const fallbackId = useId()
  const prefix = idPrefix || fallbackId
  const refs = useRef<Array<HTMLButtonElement | null>>([])

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next = index
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = tabs.length - 1
    else return
    e.preventDefault()
    refs.current[next]?.focus()
    onChange(tabs[next].key)
  }

  return (
    <div role="tablist" aria-label={label} className="flex gap-1 overflow-x-auto border-b border-line">
      {tabs.map((tab, index) => {
        const selected = tab.key === value
        return (
          <button
            key={tab.key}
            ref={el => { refs.current[index] = el }}
            type="button"
            role="tab"
            id={`${prefix}-tab-${tab.key}`}
            aria-selected={selected}
            aria-controls={`${prefix}-panel-${tab.key}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={e => onKeyDown(e, index)}
            className={cx(
              '-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm',
              'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus rounded-t',
              selected ? 'border-brand font-semibold text-ink' : 'border-transparent font-medium text-ink-secondary hover:text-ink'
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="rounded-full bg-surface-raised px-[7px] py-px text-xs font-semibold text-ink-secondary">
                {tab.count.toLocaleString()}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export function TabPanel({ tabKey, idPrefix, hidden, children }: { tabKey: string; idPrefix: string; hidden?: boolean; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`${idPrefix}-panel-${tabKey}`} aria-labelledby={`${idPrefix}-tab-${tabKey}`} hidden={hidden}>
      {children}
    </div>
  )
}
